/**
 * Realtime roster updates.
 *
 * One socket per tab, created lazily and shared, rather than one per page
 * mount: tearing the WebSocket down and rebuilding it on every route change
 * would restart the reconnect backoff and show a spurious offline blip during
 * ordinary in-app navigation.
 *
 * Auth is the same `sp_session` cookie the fetch layer uses. A same-origin
 * WebSocket sends it regardless, but `withCredentials` is set anyway so the
 * intent is visible here rather than implied.
 */
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { io, type Socket } from 'socket.io-client';
import { qk } from './api.ts';
import { weekStart } from './time.ts';

export interface RosterChanged {
  week: string;
}
export interface TimesheetChanged {
  date: string;
}
export interface NotifyEvent {
  type: string;
  payload?: Record<string, unknown>;
}

interface ServerToClient {
  'roster:changed': (p: RosterChanged) => void;
  'timesheet:changed': (p: TimesheetChanged) => void;
  notify: (p: NotifyEvent) => void;
}

let shared: Socket<ServerToClient> | null = null;

export function getSocket(): Socket<ServerToClient> {
  if (!shared) {
    shared = io({
      path: '/socket.io',
      transports: ['websocket'],
      withCredentials: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 15000,
    });
  }
  return shared;
}

export type LiveStatus = 'connecting' | 'connected' | 'offline';

/**
 * Wires the socket to the query cache for one tenant.
 *
 * `roster:changed` invalidates exactly the week it names, so a boss editing
 * week 34 is not refetching week 33 every time somebody else saves. Anything
 * the server sends for a week nobody is looking at costs nothing: an inactive
 * query invalidates without a request.
 */
export function useRealtime(
  slug: string | null,
  onNotify?: (e: NotifyEvent) => void,
): LiveStatus {
  const qc = useQueryClient();
  const [status, setStatus] = useState<LiveStatus>('connecting');

  useEffect(() => {
    if (!slug) return;
    const s = getSocket();

    const onConnect = () => setStatus('connected');
    const onDisconnect = () => setStatus('offline');
    const onError = () => setStatus('offline');

    const onRoster = (p: RosterChanged) => {
      // Normalise to a Monday: a server that names any day inside the week
      // still lands on the key the grid is holding.
      const week = weekStart(p.week);
      void qc.invalidateQueries({ queryKey: qk.roster(slug, week) });
    };
    const onTimesheet = (p: TimesheetChanged) => {
      void qc.invalidateQueries({ queryKey: qk.timesheets(slug, weekStart(p.date)) });
    };
    const onNotifyEvent = (e: NotifyEvent) => {
      void qc.invalidateQueries({ queryKey: qk.notifications(slug) });
      onNotify?.(e);
    };

    s.on('connect', onConnect);
    s.on('disconnect', onDisconnect);
    s.on('connect_error', onError);
    s.on('roster:changed', onRoster);
    s.on('timesheet:changed', onTimesheet);
    s.on('notify', onNotifyEvent);
    if (s.connected) setStatus('connected');

    return () => {
      s.off('connect', onConnect);
      s.off('disconnect', onDisconnect);
      s.off('connect_error', onError);
      s.off('roster:changed', onRoster);
      s.off('timesheet:changed', onTimesheet);
      s.off('notify', onNotifyEvent);
    };
  }, [slug, qc, onNotify]);

  return status;
}

export function closeSocket(): void {
  shared?.close();
  shared = null;
}
